/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import util from "../util/util.js";
import fieldManager from "../util/fields.js";

var program = {
	code: "cnb",
	name: "Colonial Nesting Birds",
	description: "<p>The Colonial Nesting Birds project aims to help ensure the long-term survival of colonial nesting species around Australia through population monitoring and supporting conservation and effective management of species and their habitats.</p>" +
		"<p>Please note that you must be a registered participant of a BirdLife Australia project to gain access to this form. Please speak to your Project Coordinator to arrange access.</p>",
	canParticipate: function(user) {
		return user.is_administrator || user.permissions?.some(function(p) {
			return p.type == "program" && p.program == "cnb";
		});
	},
	canFilterBy: function(user) {
		return this.canParticipate(user);
	},
	load: function() {
		return birdata.lookup().then(function(lookups) {
			program.options = lookups;
			return true;
		});
	},
	programData: function(survey) {
		var fields = program.surveyFields();

		return {
			fields: fields,
			validations: Object.values(fields)
		};
	},
	fields: function() {
		return program.surveyFields();
	},
	surveyFields: function() {
		var fields = [
			{
				name: "additionalObservers",
				type: "text",
				label: "Additional observers"
			},
			{
				name: "surveyMeans",
				type: "options",
				label: "Survey conducted by",
				options: program.options.s2020_survey_means,
				validate: "required"
			},
			{
				name: "temperatureCelsius",
				type: "number",
				label: "Temperature",
				units: "°C",
				max: 80,
				min: -20,
				decimalPlaces: 1
			},
			{
				name: "windSpeed",
				type: "options",
				label: "Wind speed",
				options: program.options.wind_speed
			},
			{
				name: "tide",
				type: "options",
				label: "Tide",
				options: program.options.s2020_tide
			},
			{
				name: "partOfFairyTernBiennialCount",
				type: "boolean",
				label: "Part of Fairy Tern census"
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		return fields;
	},
	sightingFields: function(sightingModel) {
		var species = sightingModel ? sightingModel.species : null;

		// To avoid null issues
		function colonyStageCodeIsOneOf(/*args*/) {
			var colonyStage = fields.colonyStage();
			return Array.from(arguments).includes(colonyStage?.code);
		};

		var fields = [
			{
				name: "adultCount",
				type: "integer",
				label: "Adults",
				max: 99999,
				validate: "required"
			},
			{
				name: "habitat",
				type: "options",
				label: "Habitat",
				options: program.options.bnb_habitat
			},
			{
				name: "colonyStage",
				type: "options",
				label: "Colony stage",
				options: program.options.cnb_colony_stage,
				validate: "required",
				required: true
			},
			// Colony-stage-specific fields
			{
				name: "juvenileCount",
				type: "integer",
				min: 1,
				max: 99999,
				label: "Juveniles"
			},
			{
				name: "chickCount",
				type: "integer",
				min: 1,
				hidden: () => !colonyStageCodeIsOneOf("colony_with_chicks_juveniles", "colony_with_nests_chicks_juveniles"),
				label: "Number of chicks"
			},
			{
				name: "occupiedNestCount",
				type: "integer",
				min: 1,
				hidden: () => !colonyStageCodeIsOneOf("colony_with_nests", "colony_with_nests_chicks_juveniles", "suspect_nesting"),
				label: "Number of occupied nests"
			},
			{
				name: "incompleteCount",
				type: "boolean",
				hidden: () => !colonyStageCodeIsOneOf("colony_with_chicks_juveniles", "colony_with_nests_chicks_juveniles"),
				label: "Incomplete count of nest/chicks/juveniles"
			},
			{
				name: "chickAges",
				type: "text",
				hidden: () => !colonyStageCodeIsOneOf("colony_with_chicks_juveniles", "colony_with_nests_chicks_juveniles"),
				label: "Description of chick ages"
			},
			{
				name: "scrapesPresent",
				type: "boolean",
				hidden: () => !colonyStageCodeIsOneOf("colony_establishing"),
			},
			// Behaviours
			{
				name: "aerialDisplays",
				type: "boolean"
			},
			{
				name: "courtshipFeeding",
				type: "boolean"
			},
			{
				name: "nestScrapeMaking",
				type: "boolean"
			},
			{
				name: "activeBurrow",
				type: "boolean"
			},
			{
				name: "fishWiping",
				type: "boolean"
			},
			{
				name: "incubating",
				type: "boolean"
			},
			{
				name: "diveBombing",
				type: "boolean"
			},
			{
				name: "calling",
				type: "boolean"
			},
			{
				name: "mating",
				type: "boolean"
			},
			{
				name: "brooding",
				type: "boolean"
			},
			{
				name: "feedingChicks",
				type: "boolean"
			},
			{
				name: "roosting",
				type: "boolean"
			},
			{
				name: "colonyPartialFailure",
				label: "Colony failure",
				type: "boolean",
				allowNull: true,
				defaultValue: null,
				optionLabels: ["Partial Failure", "Complete Failure", "No Failure"]

			},
			// Failure fields
			{
				name: "colonyFailure",
				type: "compound",
				minCount: 0,
				maxCount: 1,
				fields: [
					{
						name: "partialFailure",
						type: "boolean",
					},
					{
						name: "catPredation",
						type: "boolean"
					},
					{
						name: "dogPredation",
						type: "boolean"
					},
					{
						name: "foxPredation",
						type: "boolean"
					},
					{
						name: "avianPredationSpecies",
						type: "text",
						label: "Avian predator"
					},
					{
						name: "tidalInundation",
						type: "boolean"
					},
					{
						name: "deadChickCount",
						type: "integer",
						min: 1,
						label: "Number of dead chicks"
					},
					{
						name: "unattendedEggCount",
						type: "integer",
						min: 1,
						label: "Number of unattended eggs"
					},
					{
						name: "sandCover",
						type: "boolean"
					},
					{
						name: "humanActivity",
						type: "boolean"
					},
					{
						name: "unknownCause",
						type: "boolean"
					},
					{
						name: "otherCause",
						type: "text",
						label: "Other cause(s)"
					}
				]
			},
			{
				name: "tags",
				type: "multi-text",
				label: "Bands or flags",
				help: "e.g. PE Right (White); Red/Metal Left, Yellow/Orange Right; orange Right unknown ID",
				addLabel: "Add bands/flags for an individual"
			},
			{
				name: "threats",
				type: "compound",
				minCount: 0,
				maxCount: 1,
				fields: [
					{
						name: "accessWithinView",
						type: "boolean",
						validate: "required"
					},
					{
						name: "humanCount",
						type: "integer",
						label: "# People",
						defaultValue: "0",
						min: 0,
						max: 999
					},
					{
						name: "humanPrintsAboveHighTide",
						type: "boolean",
						label: "Human prints above high tide mark",
						validate: "required"
					},
					{
						name: "duneUse",
						type: "boolean",
						validate: "required"
					},
					{
						name: "fourWheelDriveEvidence",
						type: "boolean",
						label: "4WDs",
						validate: "required"
					},
					{
						name: "trailBikeEvidence",
						type: "boolean",
						validate: "required",
						label: "Trail bikes"
					},
					{
						name: "quadBikeEvidence",
						type: "boolean",
						validate: "required",
						label: "Quad bikes"
					},
					{
						name: "dogsOnLeashCount",
						type: "integer",
						label: "# Dogs on leash",
						defaultValue: "0",
						min: 0,
						max: 999
					},
					{
						name: "dogsOffLeashCount",
						type: "integer",
						label: "# Dogs off leash",
						defaultValue: "0",
						min: 0,
						max: 999
					},
					{
						name: "dogPrints",
						type: "boolean",
						validate: "required"
					},
					{
						name: "foxEvidence",
						type: "boolean",
						validate: "required",
						label: "Foxes"
					},
					{
						name: "catEvidence",
						type: "boolean",
						validate: "required",
						label: "Cats"
					},
					{
						name: "ratEvidence",
						type: "boolean",
						validate: "required",
						label: "Rats"
					},
					{
						name: "stockEvidence",
						type: "boolean",
						validate: "required",
						label: "Stock"
					},
					{
						name: "horseEvidence",
						type: "boolean",
						validate: "required",
						label: "Horses"
					},
					{
						name: "pigEvidence",
						type: "boolean",
						validate: "required",
						label: "Pigs"
					},
					{
						name: "magpieCount",
						type: "integer",
						defaultValue: "0",
						min: 0,
						max: 999,
						label: "# Magpies"
					},
					{
						name: "ravenCount",
						type: "integer",
						defaultValue: "0",
						min: 0,
						max: 999,
						label: "# Ravens"
					},
					{
						name: "silverGullCount",
						type: "integer",
						defaultValue: "0",
						min: 0,
						max: 999,
						label: "# Silver gulls"
					},
					{
						name: "pacificKelpGullCount",
						type: "integer",
						defaultValue: "0",
						min: 0,
						max: 999,
						label: "# Pacific/Kelp gulls"
					},
					{
						name: "raptorCount",
						type: "integer",
						defaultValue: "0",
						min: 0,
						max: 999,
						label: "# Raptors"
					},
					{
						name: "encroachingVegetation",
						type: "boolean",
						validate: "required"
					},
					{
						name: "otherThreats",
						type: "text",
						multiline: true,
						label: "Additional threats"
					}
				]
			},
			{
				name: "siteManagement",
				type: "compound",
				minCount: 0,
				maxCount: 1,
				fields: [
					{
						name: "permanentSign",
						type: "boolean",
						validate: "required"
					},
					{
						name: "temporarySign",
						type: "boolean",
						validate: "required"
					},
					{
						name: "fencing",
						type: "boolean",
						validate: "required"
					},
					{
						name: "cameras",
						type: "boolean",
						validate: "required"
					},
					{
						name: "foxControl",
						type: "boolean",
						validate: "required"
					},
					{
						name: "otherManagement",
						type: "text",
						multiline: true,
						label: "Other site management details"
					}
				]
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		fields.colonyPartialFailure.subscribe(function(x) {
			fields.colonyFailure.field.exists(x != null);
			if(x != null) {
				fields.colonyFailure().partialFailure(x);
			}
		});
		fields.colonyPartialFailure.field.load = function(json) {
			this.value(json.colony_failure ? json.colony_failure.partial_failure : null);
		}
		fields.colonyPartialFailure.field.save = function(json) {}

		return fields;
	},
	updateSurveyJSON: function(json, survey) {
		var fields = survey.programData().cnb.fields;
		json.cnb = {};
		json.all_species_recorded = false;
		for(let sighting of json.sightings) {
			sighting.count = (+sighting.cnb.adult_count || 0) + (+sighting.cnb.juvenile_count || 0) + (+sighting.cnb.chick_count || 0);
		}

		for(let f of Object.values(fields)) {
			f.field.save(json.cnb);
		}
	},
	hideWaterLevel: true,
	hideSurveyType: true,
	surveyTypeOptions: [],
	afterSelect: function(survey) {
		// Set survey type after program selected
		survey.type({
			code: 'colony_survey',
			name: 'Colony Survey'
		});
		survey.allSpeciesRecorded(false);
	},
	beforeDeselect: function(survey) {
		survey.type(null);
	},
	loadFromJSON: function(json, fields, survey) {
		var data = json.cnb;

		if(!data) return; //incomplete survey

		for(let f of Object.values(fields)) {
			f.field.load(data);
		}
	},
	visibleSharedSiteLayers: () => ['cnb'],
	defaultAreaLayer: (layers) => null,
	updateMap: function(map, survey, areasPromise, locationViewModel) {
	},
	routeBasedEntry: false,
	multipleSightingsPerSpecies: true,
	sightingLocations: true,
	forceSightingLocations: true,
	sightingNotesPlaceholder: 'Additional notes not captured elsewhere',
	allSpeciesRecordedEnabled: function() { return false; },
	multipleDaysEnabled: function() { return false; },
	observerCountEnabled: function() { return true; },
	showSiteProjectSelector: true,
	forcePrivate: true
};

export default program;
